import { accessor } from '../store';
import { messages } from 'src/i18n/i18n';
import { RouterViewSlider } from '../components/RouterViewSlider';
import {
    RouteEntityDetail,
    entityDetailRoute,
} from '@redviking/argonaut-core-ui/src/components/EntityDetail';
import { GetContextDocument } from 'types/db';
import { entityPageBannerTitle } from 'src/util';
import { gqlClient } from '@redviking/argonaut-core-ui/src/util/gql-client';
import { Notify } from 'src/notifications';
import {
    type ContextEntity,
    saveContext,
} from './context.entity';
import { validateContextManageAcl } from './context.validations';

export default [
    {
        path: '/context',
        name: 'context',
        component: RouterViewSlider,
        redirect: { name: 'contextList' },
        meta: {
            navbarTitle: messages.context.titles.nav,
            navbarIcon: '$argo-context',
            transition: 'horizontal',
            featureFlags: [],
        },
        children: [
            {
                name: 'contextList',
                path: '',
                component: () => import(/* webpackChunkName: "context" */ './ContextView.vue'),
                meta: {
                    pageBannerTitle: messages.context.titles.pageBannerTitle,
                    pageBannerSubtitle: messages.context.titles.pageBannerSubtitle,
                },
            },
            entityDetailRoute<'context'>({
                path: ':contextId',
                name: 'contextMaintenence',
                component: RouteEntityDetail,
                redirect: to => ({ name: 'contextSettings', params: to.params }),
                meta: {
                    entityType: 'context',
                    async getEntity (to) {
                        if (to.query.mode === 'create') {
                            return {
                                entity: {
                                    users: [],
                                    enabled: true,
                                    description: '',
                                    name: 'New Context',
                                    id: to.params.contextId || 'unknown',
                                    created_at: new Date().toISOString(),
                                    authSettings: {
                                        msal: {
                                            enabled: true,
                                            auth_provider_id: 'msal',
                                            created_by_id: 'b8d5420c-691c-11ec-831b-9e3d90d063e7',
                                            config: {
                                                behavior: {
                                                    tennantIds: [],
                                                    syncRoles: false,
                                                    allowManagement: false,
                                                },
                                            },
                                        },
                                        local: {
                                            enabled: true,
                                            auth_provider_id: 'local',
                                            created_by_id: 'b8d5420c-691c-11ec-831b-9e3d90d063e7',
                                            config: {
                                                passwordPolicy: {
                                                    minLength: 8,
                                                    numbers: true,
                                                    lowercase: true,
                                                    uppercase: true,
                                                    specialChars: true,
                                                },
                                            },
                                        },
                                    },
                                },
                            };
                        }
                        const {
                            argo_user,
                            argo_context_by_pk,
                            argo_auth_provider_msal_setting,
                            argo_auth_provider_local_setting,
                        } = await gqlClient.request({
                            document: GetContextDocument,
                            variables: {
                                contextId: to.params.contextId,
                            },
                        });
                        const localAuth = argo_auth_provider_local_setting[0];
                        const msalAuth = argo_auth_provider_msal_setting[0];
                        if (!argo_context_by_pk || !localAuth || !msalAuth) {
                            const err = new Error('Unknown Context');
                            Notify.error(err);
                            throw err;
                        }
                        const contextEntity: ContextEntity = {
                            id: argo_context_by_pk.id,
                            name: argo_context_by_pk.name,
                            enabled: argo_context_by_pk.enabled,
                            created_at: argo_context_by_pk.created_at,
                            description: argo_context_by_pk.description,
                            users: argo_user.map(u => ({
                                userId: u.id,
                                roleIds: u.roles.map(r => r.id || ''),
                            })),
                            authSettings: {
                                msal: msalAuth,
                                local: localAuth,
                            },
                        };
                        return {
                            entity: contextEntity,
                        };
                    },
                    saveEntity: saveContext,
                    returnRoute: { name: 'contextList' },
                    pageBannerTitle: () => entityPageBannerTitle('context', accessor.entityAsType('context')?.name),

                    canEdit: validateContextManageAcl,
                },
                children: [
                    {
                        path: 'settings',
                        name: 'contextSettings',
                        component: () => import(/* webpackChunkName: "context" */ './ContextSettings.view.vue'), // a component for the settings page content
                        meta: {
                            tab: { // the presence of this prop indicates this is a tab
                                label: 'Settings',
                                icon: 'mdi-cog',
                            },
                        },
                    },
                    {
                        path: 'authentication',
                        name: 'contextAuthentication',
                        component: () => import(/* webpackChunkName: "context" */ './ContextAuthentication.view.vue'), // a component for the settings page content
                        meta: {
                            tab: { // the presence of this prop indicates this is a tab
                                label: 'Authentication',
                                icon: 'mdi-lock',
                            },
                        },
                    },
                    {
                        path: 'users',
                        name: 'contextUsers',
                        component: () => import(/* webpackChunkName: "context" */ './ContextUsers.view.vue'), // a component for the settings page content
                        hide: route => {
                            // hide the users tab if the context is admin
                            return route.params.contextId === '6e8e9f0c-4c51-4f36-887c-7be7b32bd9a2';
                        },
                        meta: {
                            tab: { // the presence of this prop indicates this is a tab
                                label: 'Users',
                                icon: 'mdi-account-box-multiple',
                            },
                        },
                    },
                ],
            }),
        ],
    },
];
